import type { InventoryFloorplan, NewMeterLocationClient } from '@/features/inventorization/new-meter-location-client';
import type { FloorplanBlobRepository } from '@/shared/db/repositories/floorplan-blob-repository';
import { FLOORPLAN_BLOB_SOURCES, type FloorplanBlobRecord } from '@/shared/db/schema';

type FloorplanBlobStore = Pick<FloorplanBlobRepository, 'get' | 'save'>;
type FloorplanClient = Pick<NewMeterLocationClient, 'findFloorplan' | 'getFloorplanContent'>;

const FLOORPLAN_MAX_EDGE = 2400;
const FLOORPLAN_COMPRESS_MIN_BYTES = 2 * 1024 * 1024;
const FLOORPLAN_JPEG_QUALITY = 0.82;

export interface LoadedMeterLocationFloorplan {
  floorplan: InventoryFloorplan | null;
  floorplanRecord: FloorplanBlobRecord | null;
  imageBlob: Blob | null;
}

export async function loadMeterLocationFloorplan(input: {
  buildingId: string | null;
  client: FloorplanClient;
  floor: string;
  floorplanBlobs: FloorplanBlobStore;
  locationApiKey: string;
  locationId: number;
}): Promise<LoadedMeterLocationFloorplan> {
  const cached = await input.floorplanBlobs.get({
    buildingFloor: input.floor,
    buildingId: input.buildingId,
    locationId: input.locationId,
  });

  if (cached) {
    return {
      floorplan: createFloorplanFromRecord(cached),
      floorplanRecord: cached,
      imageBlob: cached.blob,
    };
  }

  const floorplan = await input.client.findFloorplan({
    buildingId: input.buildingId,
    floor: input.floor,
    locationApiKey: input.locationApiKey,
    locationId: input.locationId,
  });

  if (!floorplan) {
    return { floorplan: null, floorplanRecord: null, imageBlob: null };
  }

  const content = await input.client.getFloorplanContent({
    floorplanId: floorplan.id,
    locationApiKey: input.locationApiKey,
  });
  const storedImage = await prepareFloorplanImageForStorage(content);
  const savedRecord = await input.floorplanBlobs.save({
    blob: storedImage.blob,
    buildingFloor: input.floor,
    buildingId: input.buildingId,
    floorplanId: floorplan.id,
    height: floorplan.height ?? storedImage.height,
    loadedAt: new Date().toISOString(),
    locationId: input.locationId,
    mimeType: storedImage.blob.type || content.type || 'image/jpeg',
    name: floorplan.name,
    pendingUpload: false,
    source: FLOORPLAN_BLOB_SOURCES.remote,
    width: floorplan.width ?? storedImage.width,
  });

  return {
    floorplan,
    floorplanRecord: savedRecord,
    imageBlob: savedRecord.blob,
  };
}

export async function saveLocalMeterLocationFloorplan(input: {
  buildingId: string | null;
  file: File;
  floor: string;
  floorplanBlobs: FloorplanBlobStore;
  locationId: number;
}) {
  const storedImage = await prepareFloorplanImageForStorage(input.file);
  const savedRecord = await input.floorplanBlobs.save({
    blob: storedImage.blob,
    buildingFloor: input.floor,
    buildingId: input.buildingId,
    floorplanId: `local:${Date.now()}`,
    height: storedImage.height,
    loadedAt: new Date().toISOString(),
    locationId: input.locationId,
    mimeType: storedImage.blob.type || input.file.type || 'image/jpeg',
    name: readStoredFloorplanName(input.file.name, storedImage.blob),
    pendingUpload: true,
    source: FLOORPLAN_BLOB_SOURCES.local,
    width: storedImage.width,
  });

  return {
    floorplan: createFloorplanFromRecord(savedRecord),
    floorplanRecord: savedRecord,
  };
}

interface StoredFloorplanImage {
  blob: Blob;
  height: number;
  width: number;
}

async function prepareFloorplanImageForStorage(blob: Blob): Promise<StoredFloorplanImage> {
  const image = await loadImageForResize(blob);

  try {
    const width = readImageWidth(image);
    const height = readImageHeight(image);
    const compressedSize = readCompressedFloorplanSize({ height, sizeBytes: blob.size, width });

    if (!compressedSize) {
      return { blob, height, width };
    }

    const canvas = document.createElement('canvas');
    canvas.width = compressedSize.width;
    canvas.height = compressedSize.height;

    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    }

    const compressedBlob = await canvasToBlob(canvas);

    if (compressedBlob.size >= blob.size) {
      return { blob, height, width };
    }

    return {
      blob: compressedBlob,
      height: compressedSize.height,
      width: compressedSize.width,
    };
  } finally {
    closeImageForResize(image);
  }
}

export function readCompressedFloorplanSize(input: { height: number; sizeBytes: number; width: number }) {
  if (
    !(Number.isFinite(input.height) && Number.isFinite(input.width)) ||
    input.height <= 0 ||
    input.width <= 0 ||
    (input.sizeBytes < FLOORPLAN_COMPRESS_MIN_BYTES && Math.max(input.height, input.width) <= FLOORPLAN_MAX_EDGE)
  ) {
    return null;
  }

  const scale = Math.min(1, FLOORPLAN_MAX_EDGE / Math.max(input.height, input.width));

  return {
    height: Math.max(1, Math.round(input.height * scale)),
    width: Math.max(1, Math.round(input.width * scale)),
  };
}

function createFloorplanFromRecord(record: FloorplanBlobRecord): InventoryFloorplan {
  return {
    floor: record.buildingFloor,
    height: record.height,
    id: record.floorplanId,
    name: record.name ?? '',
    width: record.width,
  };
}

function loadImageForResize(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file).catch(() => loadHtmlImage(file));
  }

  return loadHtmlImage(file);
}

function loadHtmlImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read floorplan image dimensions.'));
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to encode compressed floorplan.'));
        }
      },
      'image/jpeg',
      FLOORPLAN_JPEG_QUALITY,
    );
  });
}

function readImageWidth(image: ImageBitmap | HTMLImageElement) {
  return 'naturalWidth' in image ? image.naturalWidth : image.width;
}

function readImageHeight(image: ImageBitmap | HTMLImageElement) {
  return 'naturalHeight' in image ? image.naturalHeight : image.height;
}

function closeImageForResize(image: ImageBitmap | HTMLImageElement) {
  if ('close' in image) {
    image.close();
  }
}

function readStoredFloorplanName(name: string, blob: Blob) {
  const trimmed = name.trim() || 'local-floorplan';

  if (blob.type !== 'image/jpeg') {
    return trimmed;
  }

  return /\.[^.]+$/.test(trimmed) ? trimmed.replace(/\.[^.]+$/, '.jpg') : `${trimmed}.jpg`;
}
